import React, {
  useEffect,
  useState,
  createContext,
  FC,
  useContext,
  useMemo,
} from "react";
import { onAuthStateChanged, User } from "firebase/auth";
import { auth } from "../firebase/config";
// import { createOrLoadStudentProfile } from "../api/private/students";
import {
  signInWithEmail as utilSignInWithEmail,
  logOut as utilLogOut,
} from "../firebase/utils";
import { createOrLoadMyProfile } from "../api/admin/users";
import { notify } from "../lib/utils";

type UserProfile = any;

interface AuthContextValue {
  user: User | null;
  userProfile: UserProfile | null;
  isLoggedIn: boolean;
  isCheckingAuth: boolean;
  signInWithEmail: (email: string, pass: string) => Promise<void>;
  logOut: () => Promise<void>;
  setUserProfile: React.Dispatch<React.SetStateAction<UserProfile | null>>;
  btnLoading: boolean;
}

// Create context
const authContext = createContext<AuthContextValue | undefined>(undefined);
authContext.displayName = "AuthContext";

// Consumer hook
export const useAuth = () => {
  const context = useContext(authContext);
  if (context === undefined) {
    throw new Error(`useAuth must be used within a AuthProvider`);
  }
  return context;
};

// Provider component
export const AuthProvider: FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  // for buttons sign in/ sign up
  const [btnLoading, setBtnLoading] = useState(false);

  const handleProfileCreationOrLoad = async () => {
    const response = await createOrLoadMyProfile();

    if (!response || response.status !== 201) {
      console.log("Error creating or loading profile");
      notify({
        type: "error",
        message: "Error creating or loading profile",
      });

      setUserProfile(null);
      return;
    }

    setUserProfile(response.data);
  };

  const checkAuthState = () => {
    onAuthStateChanged(auth, async (user) => {
      if (user) {
        handleProfileCreationOrLoad();
        setUser(user);
        setIsLoggedIn(true);
        setIsCheckingAuth(false);
      } else {
        setUser(null);
        setIsLoggedIn(false);
        setIsCheckingAuth(false);
      }
    });
  };

  useEffect(() => {
    checkAuthState();
    return () => {
      setIsCheckingAuth(true);
      setIsLoggedIn(true);
      setUser(null);
      setUserProfile(null);
    };
  }, []);

  const signInWithEmail = async (email: string, pass: string) => {
    utilSignInWithEmail(auth, email, pass, setBtnLoading);
  };

  const logOut = async () => {
    utilLogOut(auth);
  };

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      userProfile,
      isLoggedIn,
      isCheckingAuth,
      signInWithEmail,
      logOut,
      setUserProfile,
      btnLoading,
    }),
    [user, userProfile, isLoggedIn, isCheckingAuth, btnLoading]
  );

  return <authContext.Provider value={value}>{children}</authContext.Provider>;
};
